import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

type ListValue = string | string[] | number | boolean;

const read = (params: URLSearchParams, key: string, fallback: ListValue) => {
  const raw = params.get(key);
  if (raw === null) return fallback;
  if (Array.isArray(fallback)) return raw.split(",").filter(Boolean);
  if (typeof fallback === "number") {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  if (typeof fallback === "boolean") return raw === "1";
  return raw;
};

const write = (value: ListValue) => {
  if (Array.isArray(value)) return value.join(",");
  if (typeof value === "boolean") return value ? "1" : "0";
  return String(value);
};

const isDefault = (value: ListValue, fallback: ListValue) =>
  Array.isArray(value) && Array.isArray(fallback)
    ? value.length === 0 && fallback.length === 0
    : value === fallback;

/**
 * A list screen's search, filters, tab and page, kept in the URL.
 *
 * Opening a row and coming back (browser back, or a "Back to …" button that
 * returns to the list's own URL) lands on the same results instead of the full
 * list, and a filtered list can be bookmarked or shared.
 *
 * `defaults` names every key and its type: a string, a string[] (written
 * comma-separated), a number or a boolean. A value equal to its default is left
 * out of the URL, so an untouched list keeps a clean address. Keys this hook
 * does not own (a selected row, say) are left as they are.
 *
 * Writes use `replace`, so typing in a search box does not add history entries
 * and one press of back always leaves the list. Changing any key other than
 * `page` sends `page` back to its default: a new search starts on page 1.
 */
export function useUrlListState<T extends Record<string, ListValue>>(
  defaults: T
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => {
    const next = {} as T;
    for (const key of Object.keys(defaults) as (keyof T & string)[]) {
      next[key] = read(searchParams, key, defaults[key]) as T[typeof key];
    }
    return next;
    // `defaults` is a literal at each call site; only the URL drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const update = useCallback(
    (changes: Partial<T>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          const entries = Object.entries(changes) as [
            keyof T & string,
            ListValue,
          ][];
          if ("page" in defaults && !("page" in changes)) {
            entries.push(["page", defaults.page]);
          }
          for (const [key, value] of entries) {
            if (isDefault(value, defaults[key])) next.delete(key);
            else next.set(key, write(value));
          }
          return next;
        },
        { replace: true }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );

  return [state, update] as const;
}
